"use client";

import { Checkbox as ArkCheckbox } from "@ark-ui/react/checkbox";
import { IconCheck, IconMinus } from "@decibeltrade/react-icons";
import { focusRing } from "@decibeltrade/web-core/recipes";
import { forwardRef, ReactNode, useRef, useState } from "react";
import { css, cva, cx, type RecipeVariantProps } from "styled-system/css";
import { flex } from "styled-system/patterns";

export type CheckboxVariant = NonNullable<RecipeVariantProps<typeof controlStyles>>["variant"];

export interface CheckboxProps {
  /** The label for the checkbox */
  children?: ReactNode;
  /** Whether or not the checkbox is checked */
  checked?: boolean | "indeterminate";
  /** The callback that runs each time the checkbox is toggled */
  onChange?: (checked: boolean | "indeterminate") => void;
  /** If true, the checkbox will be disabled. */
  disabled?: boolean;
  /** The name attribute of the underlying input element. Useful for form submission. */
  name?: string;
  /** Class name for applying additional styles to the checkbox's wrapper element */
  className?: string;
  /** Whether to render the checkbox to the left or right of the label */
  checkboxPosition?: "left" | "right";
  /** The callback that runs when the keyboard is used to toggle the checkbox */
  onKeyDown?: (e: React.KeyboardEvent<HTMLLabelElement>) => void;
  /**
   * Visual variant. `"default"` is the default neutral checkbox;
   * `"danger"` uses negative-themed colors for risk-acknowledgement flows.
   */
  variant?: CheckboxVariant;
}

/** A component for selecting or deselecting a single value. */
export const Checkbox = forwardRef<HTMLLabelElement, CheckboxProps>(
  (
    {
      children,
      checked,
      onChange,
      disabled,
      name,
      className,
      checkboxPosition = "left",
      onKeyDown,
      variant = "default",
    },
    ref,
  ) => {
    const wasClicked = useRef(false);
    const [isKeyboardFocus, setIsKeyboardFocus] = useState(false);

    return (
      <ArkCheckbox.Root
        ref={ref}
        checked={checked}
        onCheckedChange={(e) => onChange?.(e.checked)}
        disabled={disabled}
        name={name}
        className={cx(root, className)}
        onMouseDown={() => {
          wasClicked.current = true;
          setIsKeyboardFocus(false);
        }}
        onFocus={() => {
          if (!wasClicked.current) {
            // Is `wasClicked.current` is not true, we can assume keyboard navigation was used
            setIsKeyboardFocus(true);
          }
          wasClicked.current = false;
        }}
        onBlur={() => {
          setIsKeyboardFocus(false);
        }}
        onKeyDown={onKeyDown}
      >
        <ArkCheckbox.Context>
          {({ checked, indeterminate }) => {
            const checkboxNode = (
              <ArkCheckbox.Control
                data-focus-visible={isKeyboardFocus || undefined}
                className={cx(controlStyles({ variant }), focusRing({ rounded: "300" }))}
              >
                {checked && <IconCheck className={controlIcon} />}
                {indeterminate && <IconMinus className={controlIcon} />}
              </ArkCheckbox.Control>
            );

            return (
              <>
                {checkboxPosition === "left" && checkboxNode}
                {children !== undefined && (
                  <ArkCheckbox.Label className={labelStyles({ variant })}>
                    {children}
                  </ArkCheckbox.Label>
                )}
                {checkboxPosition === "right" && checkboxNode}
                <ArkCheckbox.HiddenInput />
              </>
            );
          }}
        </ArkCheckbox.Context>
      </ArkCheckbox.Root>
    );
  },
);

Checkbox.displayName = "Checkbox";

const root = flex({
  align: "center",
  gap: "12",
  cursor: "pointer",
  _disabled: { cursor: "not-allowed" },
});

const controlStyles = cva({
  base: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    flexShrink: "0",
    h: "20",
    w: "20",
    rounded: "primary",
    borderStyle: "solid",
    borderWidth: "100",
    transitionPreset: "common",

    // Limit the background to the padding area so that it doesn't stack with the border-color
    // Since the border-color has transparency for some variants, we don't want the colors
    // to mix and create a darker border.
    backgroundClip: "padding-box!",

    _disabled: {
      borderColor: "checkbox.background.disabled!",
      bg: "checkbox.background.disabled",
      color: "checkbox.check.disabled",
    },
  },
  variants: {
    variant: {
      default: {
        color: "checkbox.check",
        borderColor: "checkbox.border",
        bg: "checkbox.background",
        _hover: { borderColor: "checkbox.border.hover" },
        "&[data-state='checked'], &[data-state='indeterminate']": {
          borderColor: {
            base: "checkbox.background.checked",
            _hover: "checkbox.background.hover",
            _disabled: "checkbox.background.disabled",
          },
          bg: {
            base: "checkbox.background.checked",
            _hover: "checkbox.background.hover",
            _disabled: "checkbox.background.disabled",
          },
        },
      },
      danger: {
        color: "black",
        borderColor: "border.negative",
        bg: "checkbox.background",
        _hover: { borderColor: "border.negativeActive" },
        "&[data-state='checked'], &[data-state='indeterminate']": {
          borderColor: {
            base: "text.negative",
            _hover: "background.negativeActive",
            _disabled: "checkbox.background.disabled",
          },
          bg: {
            base: "text.negative",
            _hover: "background.negativeActive",
            _disabled: "checkbox.background.disabled",
          },
        },
      },
    },
  },
  defaultVariants: { variant: "default" },
});

const controlIcon = css({
  h: "16",
  w: "16",
});

const labelStyles = cva({
  base: {
    textStyle: { base: "body.lg", desktop: "body.md" },
    _disabled: { color: "text.disabled" },
  },
  variants: {
    variant: {
      default: { color: "text.secondary" },
      danger: { color: "text.negative" },
    },
  },
  defaultVariants: { variant: "default" },
});
